import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { createFetchAsrModelSettingsQueryFn } from '../../../../api/fetchAsrModelSettings';
import { ModelQualityCheckerSettings } from './components/ModelQualityCheckerSettings';
import { ModelNormalizerSettings } from './components/ModelNormalizerSettings';

export const ModelPipelineSettings = () => {
  const { call } = useAPI();
  const query = useQuery({ queryKey: asrProxyKeys.modelSettings(), queryFn: createFetchAsrModelSettingsQueryFn(call) });
  return (
    <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)] xl:col-span-2">
      <h2 className="text-lg font-medium text-foreground">Model pipeline</h2>
      <p className="mt-1 text-sm text-foreground-muted">
        Enable or disable normalizers and quality checkers registered for each model. Descriptions and execution order
        are defined by their implementations. Quality checkers score transcripts without changing text.
      </p>
      <p className="mt-2 text-sm text-foreground-muted">
        Saved settings apply when a transcription reaches normalization, including jobs already in progress. Completed
        transcripts are not changed.
      </p>
      {query.isLoading && (
        <p role="status" className="mt-4 text-sm text-foreground-muted">
          Loading model settings…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="mt-4 text-sm text-danger">
          {getApiErrorMessage(query.error) || 'Could not load model settings.'}
        </p>
      )}
      <div className="mt-4 space-y-4">
        {query.data?.map((model) => (
          <div key={model.modelName} className="space-y-4">
            <ModelNormalizerSettings model={model} />
            <ModelQualityCheckerSettings model={model} />
          </div>
        ))}
      </div>
      {query.isSuccess && query.data.length === 0 && (
        <p className="mt-4 text-sm text-foreground-muted">No models are available.</p>
      )}
      <OutlinedButton className="mt-4 !w-auto" disabled={query.isFetching} onClick={() => void query.refetch()}>
        {query.isFetching ? 'Refreshing…' : 'Refresh model settings'}
      </OutlinedButton>
    </section>
  );
};
